/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const initCart = {
  id: '',
  total: '',
  vat: '',
  amount: '',
  discount: '',
  nettotal: '',
};

function PosCartList({ parent, type }) {
  const mod = 'cart_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cart, setCart] = useState(initCart);
  const [cartList, setCartList] = useState([]);
  const [campaign, setCampaign] = useState([]);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const view = () => {
    setLoading(true);

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      ref_id: parent.id,
    }).then((result) => {
      if (result.status === 'ok') {
        setCart(result.parent);
        setCartList(result.data);
        setCampaign(result.campaign);
      }
      setLoading(false);
    });
  };

  const del = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view();
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const btnDel = (item) => {
    if (type !== 'cart') return null;

    if (item.enable === 'N') {
      return (
        <Tooltip title='ขอลบ'>
          <IconButton aria-label='delete' color='error' size='small' onClick={() => del(item)}>
            <Icon>delete</Icon>
          </IconButton>
        </Tooltip>
      );
    }

    return (
      <Tooltip title='ลบ'>
        <IconButton aria-label='delete' color='secondary' size='small' onClick={() => del(item)}>
          <Icon>delete</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const renderInfo = () => {
    const text = (title, value) => {
      return (
        <TableRow hover>
          <TableCell width={100}>
            <b>{title} :</b>
          </TableCell>
          <TableCell align='right'>{value}</TableCell>
        </TableRow>
      );
    };

    const number = (title, value) => {
      return (
        <TableRow hover>
          <TableCell width={140}>
            <b>{title} :</b>
          </TableCell>
          <TableCell align='right'>{Func.numberDisplay(value, 2)}</TableCell>
        </TableRow>
      );
    };

    const numberDiff = (title, value) => {
      return (
        <TableRow hover>
          <TableCell width={130}>
            <b>{title} :</b>
          </TableCell>
          <TableCell align='right'>
            {value > 0 ? (
              <span className='color-success'>+{Func.numberDisplay(value, 2)}</span>
            ) : value < 0 ? (
              <span className='color-danger'>{Func.numberDisplay(value, 2)}</span>
            ) : (
              Func.numberDisplay(value, 2)
            )}
          </TableCell>
        </TableRow>
      );
    };

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox className='card' p={1}>
            <MDTypography variant='h5' mb={1}>
              <i className='fad fa-cash-register' /> POS
            </MDTypography>
            <TableContainer sx={{ boxShadow: 0 }}>
              <Table aria-label='simple table'>
                <TableBody>
                  {text('สาขา', cart.branch_name)}
                  {text('รอบ', cart.shift_code)}
                  {text('POS', cart.cashier_name)}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox className='card' p={1}>
            <MDTypography variant='h5' mb={1}>
              <i className='fad fa-usd-circle' /> ลิ้นชัก
            </MDTypography>
            <TableContainer sx={{ boxShadow: 0 }}>
              <Table aria-label='simple table'>
                <TableBody>
                  {number('เงินลิ้นชัก', cart.wallet)}
                  {number('เงินทอนเริ่มต้น', cart.money_start)}
                  {number('ดรอปเงิน', cart.money_drop)}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox className='card' p={1}>
            <MDTypography variant='h5' mb={1}>
              <i className='fad fa-shopping-cart' /> ตะกร้า
            </MDTypography>
            <TableContainer sx={{ boxShadow: 0 }}>
              <Table aria-label='simple table'>
                <TableBody>
                  {text('เลขตะกร้า', cart.id)}
                  {text('วันที่ขาย', Func.dateTimeDisplay(cart.created_at))}
                  {text('ผู้ขาย', cart.user_name)}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox className='card' p={1}>
            <MDTypography variant='h5' mb={1}>
              <i className='fad fa-user' /> ลูกค้า
            </MDTypography>
            <TableContainer sx={{ boxShadow: 0 }}>
              <Table aria-label='simple table'>
                <TableBody>
                  {text('ลูกค้า', cart.client_name)}
                  {text('เบอร์โทร', cart.client_tel)}
                  {text('ประเทศ', cart.country_name)}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Grid>
      </Grid>
    );
  };

  const renderOrder = () => {
    return (
      <MDBox className='card' p={2}>
        <TableContainer sx={{ boxShadow: 0 }}>
          <Table aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell align='center' width={20}>
                  No.
                </TableCell>
                <TableCell width={100}>Barcode</TableCell>
                <TableCell>สินค้า</TableCell>
                <TableCell align='right' width={90}>
                  คงเหลือ
                </TableCell>
                <TableCell align='right' width={90}>
                  ราคา
                </TableCell>
                <TableCell align='right' width={90}>
                  จำนวน
                </TableCell>
                <TableCell align='left' width={50}>
                  หน่วย
                </TableCell>
                <TableCell align='right' width={90}>
                  รวม
                </TableCell>
                <TableCell align='right' width={40}>
                  &nbsp;
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cartList.map((row, i) => (
                <TableRow className='row-order' key={row.id}>
                  <TableCell align='center'>{i + 1}</TableCell>
                  <TableCell>{row.barcode}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell align='right'>
                    {row.stock_balance * 1 <= 0 ? (
                      <code>หมด</code>
                    ) : (
                      Func.numberDisplay(row.stock_balance)
                    )}
                  </TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.price)}</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.quantity_all)}</TableCell>
                  <TableCell align='left' className='nowrap'>
                    {row.unit_name}
                  </TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.total)}</TableCell>
                  <TableCell align='center'>{btnDel(row)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const renderSummary = () => {
    const discountRequest = () => {
      if (cart.discount_request * 1 > 0) {
        return (
          <span>
            ขอลด{' '}
            <span className='color-danger'>{Func.numberDisplay(cart.discount_request, 2)} ฿</span> :
          </span>
        );
      }

      return 'ขอส่วนลด :';
    };

    return (
      <MDBox className='card' p={2}>
        <TableContainer sx={{ boxShadow: 0 }}>
          <Table aria-label='summary table'>
            <TableBody>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    มูลค่ารวม :
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    {Func.numberDisplay(cart.total, 2)} ฿
                  </MDTypography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    คูปอง :
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  {cart.discount_coupon * 1 > 0 ? (
                    <MDTypography variant='h4' className='nowrap'>
                      {Func.numberDisplay(cart.discount_coupon, 2)} ฿
                    </MDTypography>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    โปรโมชั่น :
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  {cart.discount * 1 > 0 ? (
                    <MDTypography variant='h4' className='nowrap'>
                      {Func.numberDisplay(cart.discount, 2)} ฿
                    </MDTypography>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    คืนสินค้า :
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  {cart.problem_type === 'return' ? (
                    <MDTypography variant='h4' className='nowrap'>
                      - {Func.numberDisplay(cart.return_total, 2)} ฿
                    </MDTypography>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    {discountRequest()}
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  {cart.discount_approve * 1 > 0 ? (
                    <MDTypography variant='h4' className='nowrap'>
                      {Func.numberDisplay(cart.discount_approve, 2)} ฿
                    </MDTypography>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    รวมทั้งสิ้น :
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  <MDTypography variant='h4' color='info' className='nowrap'>
                    {Func.numberDisplay(cart.nettotal, 2)} ฿
                  </MDTypography>
                </TableCell>
              </TableRow>
              {cart.discount_request * 1 > 0 ? (
                <TableRow>
                  <TableCell align='right'>
                    <MDTypography variant='h4' className='nowrap'>
                      หลังขอส่วนลด :
                    </MDTypography>
                  </TableCell>
                  <TableCell align='right'>
                    <MDTypography variant='h4' color='error' className='nowrap'>
                      {Func.numberDisplay(cart.discount_balance, 2)} ฿
                    </MDTypography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const renderCampaign = () => {
    return (
      <MDBox className='card' p={2} mt={2}>
        <TableContainer sx={{ boxShadow: 0 }}>
          <Table aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>โปรโมชั่น</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaign.map((row, i) => (
                <TableRow className='row-order' key={row.id}>
                  <TableCell>{row.code}</TableCell>
                  <TableCell>{row.name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderInfo()}
          </Grid>
          <Grid item xs={12} md={8}>
            {renderOrder()}
            {renderCampaign()}
          </Grid>
          <Grid item xs={12} md={4}>
            {renderSummary()}
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    view();
  }, []);

  return parent.id === '' ? null : (
    <div>
      {renderList()}
      {renderLoading()}
      {notify}
    </div>
  );
}

PosCartList.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  type: PropTypes.string.isRequired,
};

export default PosCartList;
